import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageSquare, 
  Phone, 
  Mail,
  CheckCircle2,
  Loader2,
  User,
  MapPin,
  Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { DEALERSHIP_INFO } from '../config/dealership';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        subject: formData.subject.trim() || null,
        message: formData.message.trim()
      };

      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send your message');
      }

      setIsSubmitted(true);
      toast.success('Your message has been sent!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#000000] pt-20" data-testid="contact-page">
        <section className="py-20 md:py-28">
          <div className="max-w-2xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-[#0A0A0A] border border-[#CCAD60]/30 p-8 md:p-12 text-center"
              data-testid="contact-success-message"
            >
              <div className="w-20 h-20 mx-auto flex items-center justify-center bg-[#CCAD60]/20 border border-[#CCAD60]/50 mb-6">
                <CheckCircle2 size={40} className="text-[#CCAD60]" />
              </div>
              
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-[white] mb-4">
                Message Sent!
              </h1>
              
              <p className="text-[zinc-500] font-sans text-lg leading-relaxed mb-8">
                Thank you for getting in touch. We've received your message and 
                our team will respond as soon as possible.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 h-12 px-8 bg-[#CCAD60] text-black font-bold uppercase tracking-widest text-xs hover:bg-white transition-colors"
                >
                  Return Home
                </Link>
                <Link
                  to="/cars"
                  className="inline-flex items-center justify-center gap-2 h-12 px-8 bg-transparent border-2 border-white/30 text-white font-bold uppercase tracking-widest text-xs hover:border-[#CCAD60] hover:text-[#CCAD60] transition-colors"
                >
                  Browse Our Cars
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000000] pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#141414] via-[#0A0A0A] to-[#000000]" />
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#CCAD60]/5 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-[#CCAD60]/20">
                  <MessageSquare size={24} className="text-[#CCAD60]" />
                </div>
                <p className="text-[#CCAD60] font-sans text-xs uppercase tracking-[0.3em]">
                  Get In Touch
                </p>
              </div>
              
              <h1 
                className="font-serif text-4xl sm:text-5xl font-bold text-[white] leading-tight tracking-tight mb-6"
                data-testid="contact-page-title"
              >
                Contact
                <span className="text-[#CCAD60]"> McCarthy Motors</span>
              </h1>
              
              <p className="font-sans text-lg text-[zinc-500] leading-relaxed mb-8">
                Have a question or need some help? Send us a message using the form 
                and we'll get back to you as soon as possible.
              </p>
              
              <p className="font-sans text-sm text-[zinc-500] leading-relaxed mb-8">
                For specific enquiries, you can also use our dedicated forms:
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <Link
                  to="/cars"
                  className="bg-[#141414]/50 border border-white/10 p-4 hover:border-[#CCAD60]/30 transition-colors"
                >
                  <h3 className="font-serif text-sm font-bold text-[white] mb-1">
                    Buy a Car
                  </h3>
                  <p className="text-xs text-[zinc-500] font-sans">
                    Browse our inventory
                  </p>
                </Link>
                <Link
                  to="/sell-your-car"
                  className="bg-[#141414]/50 border border-white/10 p-4 hover:border-[#CCAD60]/30 transition-colors"
                >
                  <h3 className="font-serif text-sm font-bold text-[white] mb-1">
                    Sell Your Car
                  </h3>
                  <p className="text-xs text-[zinc-500] font-sans">
                    Get a valuation
                  </p>
                </Link>
                <Link
                  to="/repairs"
                  className="bg-[#141414]/50 border border-white/10 p-4 hover:border-[#CCAD60]/30 transition-colors"
                >
                  <h3 className="font-serif text-sm font-bold text-[white] mb-1">
                    Appointments
                  </h3>
                  <p className="text-xs text-[zinc-500] font-sans">
                    Book an appointment
                  </p>
                </Link>
              </div>
              
              {/* Contact Info */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#141414]">
                    <Phone size={18} className="text-[#CCAD60]" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans">Phone</p>
                    <a href={DEALERSHIP_INFO.phoneHref} className="text-[white] font-sans text-sm hover:text-[#CCAD60] transition-colors">
                      {DEALERSHIP_INFO.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#141414]">
                    <Mail size={18} className="text-[#CCAD60]" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans">Email</p>
                    <a href={DEALERSHIP_INFO.emailHref} className="text-[white] font-sans text-sm hover:text-[#CCAD60] transition-colors">
                      {DEALERSHIP_INFO.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#141414]">
                    <MapPin size={18} className="text-[#CCAD60]" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans">Location</p>
                    <p className="text-[white] font-sans text-sm">
                      {DEALERSHIP_INFO.address.full}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#141414]">
                    <Clock size={18} className="text-[#CCAD60]" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans">Hours</p>
                    <p className="text-[white] font-sans text-sm">
                      {DEALERSHIP_INFO.hoursSummary}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <form 
                onSubmit={handleSubmit} 
                className="bg-[#0A0A0A] border border-white/10 p-6 md:p-8"
                data-testid="contact-form"
              >
                <h2 className="font-serif text-xl font-bold text-[white] mb-6">
                  Send Us a Message
                </h2>
                
                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      className={`bg-[#141414] border-white/10 text-[white] h-11 focus:border-[#CCAD60] ${errors.name ? 'border-red-500' : ''}`}
                      data-testid="contact-input-name"
                    />
                    {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className={`bg-[#141414] border-white/10 text-[white] h-11 focus:border-[#CCAD60] ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="contact-input-email"
                    />
                    {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                  </div>
                  
                  {/* Phone (Optional) */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
                      Phone <span className="text-[zinc-500]">(optional)</span>
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="07700 000 000"
                      className="bg-[#141414] border-white/10 text-[white] h-11 focus:border-[#CCAD60]"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  
                  {/* Subject (Optional) */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
                      Subject <span className="text-[zinc-500]">(optional)</span>
                    </label>
                    <Input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="What's this about?"
                      className="bg-[#141414] border-white/10 text-[white] h-11 focus:border-[#CCAD60]"
                      data-testid="contact-input-subject"
                    />
                  </div>
                  
                  {/* Message */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="How can we help you?"
                      rows={5}
                      className={`bg-[#141414] border-white/10 text-[white] focus:border-[#CCAD60] resize-none ${errors.message ? 'border-red-500' : ''}`}
                      data-testid="contact-input-message"
                    />
                    {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
                  </div>
                </div>
                
                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 mt-6 bg-[#CCAD60] text-black font-bold uppercase tracking-widest text-xs hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <MessageSquare size={18} className="mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-[zinc-500] text-center mt-4">
                  We typically respond within 24 hours.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
